import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, PawPrint, FileText, Calendar,
  Shield, Mail, Clock, BarChart3, Plus, Trash2, Crown, UserPlus,
  AlertTriangle, RefreshCw, CheckCircle, XCircle, ChevronDown, ChevronUp,
  Search, Dog, File, Stethoscope, Database
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Accesso non autorizzato");
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, adminsRes, unverifiedRes, usersRes] = await Promise.all([
        authFetch(`${API}/admin/stats`),
        authFetch(`${API}/admin/admins`),
        authFetch(`${API}/admin/users/unverified`),
        authFetch(`${API}/admin/users?limit=100`)
      ]);
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
        setRecentUsers(data.recent_users || []);
      }
      
      if (adminsRes.ok) {
        const data = await adminsRes.json();
        setAdmins(data.admins || []);
      }
      
      if (unverifiedRes.ok) {
        const data = await unverifiedRes.json();
        setUnverifiedUsers(data.unverified_users || []);
      }
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setAllUsers(data.users || []);
        setFilteredUsers(data.users || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter users by search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(allUsers);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredUsers(allUsers.filter(u => 
        u.email?.toLowerCase().includes(query) ||
        u.name?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, allUsers]);

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return "Mai";
    const date = new Date(lastLogin);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Adesso";
    if (diffMins < 60) return `${diffMins}m fa`;
    if (diffHours < 24) return `${diffHours}h fa`;
    if (diffDays < 7) return `${diffDays}g fa`;
    return date.toLocaleDateString('it-IT');
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) return;
    
    setAddingAdmin(true);
    try {
      const response = await authFetch(`${API}/admin/admins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newAdminEmail.trim() })
      });
      
      if (response.ok) {
        toast.success(`${newAdminEmail} aggiunto come admin`);
        setNewAdminEmail("");
        setShowAddAdmin(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiunta");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!confirm(`Rimuovere ${email} dagli admin?`)) return;
    
    try {
      const response = await authFetch(`${API}/admin/admins/${encodeURIComponent(email)}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success(`${email} rimosso dagli admin`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella rimozione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`Eliminare definitivamente l'utente ${email}?`)) return;
    
    try {
      const response = await authFetch(`${API}/admin/users/${userId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success(`${email} eliminato`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleCleanupUnverified = async () => {
    if (!confirm(`Eliminare tutti gli utenti non verificati con tempo scaduto (>1 ora)?`)) return;
    
    setCleaningUp(true);
    try {
      const response = await authFetch(`${API}/admin/users/unverified/cleanup`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel cleanup");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF6B4A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: "Utenti Totali", value: stats?.total_users || 0, icon: Users, color: "#3B82F6", bgColor: "bg-blue-50 dark:bg-blue-900/20" },
    { label: "Animali", value: stats?.total_pets || 0, icon: PawPrint, color: "#FF6B4A", bgColor: "bg-orange-50 dark:bg-orange-900/20" },
    { label: "Documenti", value: stats?.total_documents || 0, icon: FileText, color: "#10B981", bgColor: "bg-emerald-50 dark:bg-emerald-900/20" },
    { label: "Prenotazioni", value: stats?.total_bookings || 0, icon: Calendar, color: "#8B5CF6", bgColor: "bg-purple-50 dark:bg-purple-900/20" },
    { label: "Professionisti", value: stats?.total_professionals || 0, icon: Shield, color: "#F59E0B", bgColor: "bg-amber-50 dark:bg-amber-900/20" },
    { label: "In Attesa", value: stats?.pending_professionals || 0, icon: Clock, color: "#EF4444", bgColor: "bg-red-50 dark:bg-red-900/20" }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)} 
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white">Dashboard Admin</h1>
              <p className="text-sm text-[#86868B]">Panoramica del sistema</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`${stat.bgColor} rounded-2xl p-6 border border-black/5 dark:border-white/10`}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                    <Icon size={24} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#1D1D1F] dark:text-white">{stat.value}</p>
                    <p className="text-sm text-[#86868B] dark:text-gray-400">{stat.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Vet Verification Card */}
          <Link 
            to="/admin/vets"
            className="bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] rounded-2xl p-6 border border-black/5 dark:border-white/10 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <Stethoscope size={28} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Verifica Veterinari</h3>
                  <p className="text-sm text-white/80">Gestisci account veterinari e cliniche</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{stats?.pending_vets || 0}</p>
                <p className="text-xs text-white/70">in attesa</p>
              </div>
            </div>
          </Link>

          {/* Knowledge Base Card */}
          <Link 
            to="/admin/kb"
            className="bg-gradient-to-br from-[#3B82F6] to-[#6366F1] rounded-2xl p-6 border border-black/5 dark:border-white/10 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <Database size={28} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Knowledge Base Vaccini</h3>
                  <p className="text-sm text-white/80">Database vaccini autorizzati EMA/AIFA</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{stats?.kb_vaccines || 0}</p>
                <p className="text-xs text-white/70">vaccini</p>
              </div>
            </div>
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">

          {/* Admin Management */}
          <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
                  <Crown className="text-[#FF6B4A]" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1D1D1F] dark:text-white">Amministratori</h3>
                  <p className="text-xs text-[#86868B]">{admins.length} admin attivi</p>
                </div>
              </div>
              <Button
                onClick={() => setShowAddAdmin(true)}
                size="sm"
                className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
              >
                <Plus size={16} className="mr-1" />
                Aggiungi
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {admins.map((admin, index) => {
                const isOwner = admin.email === "giovanniperani@gmail.com";
                const isSelf = admin.email === user?.email;
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-[#86868B]" />
                      <span className="text-sm text-[#1D1D1F] dark:text-white">{admin.email}</span>
                      {isOwner && (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
                          Owner
                        </span>
                      )}
                      {isSelf && !isOwner && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                          Tu
                        </span>
                      )}
                    </div>
                    {!isOwner && !isSelf && (
                      <button
                        onClick={() => handleRemoveAdmin(admin.email)}
                        className="p-2 text-[#86868B] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            
            <p className="text-xs text-[#86868B] mt-4">
              Gli admin hanno accesso a questa dashboard e possono gestire servizi e utenti. 
              Solo gli admin possono aggiungere altri admin.
            </p>
          </div>

          {/* Recent Users */}
          <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <UserPlus className="text-blue-500" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1D1D1F] dark:text-white">Ultimi Iscritti</h3>
                  <p className="text-xs text-[#86868B]">Solo email (privacy)</p>
                </div>
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentUsers.length > 0 ? (
                recentUsers.map((u, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl">
                    <Mail size={16} className="text-[#86868B]" />
                    <span className="text-sm text-[#1D1D1F] dark:text-white">{typeof u === 'string' ? u : u.email}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-[#86868B]">Nessun utente recente</p>
              )}
            </div>
          </div>
        </div>

        {/* Unverified Users Section */}
        {unverifiedUsers.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="text-amber-600" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-800 dark:text-amber-300">Utenti Non Verificati</h3>
                  <p className="text-xs text-amber-600 dark:text-amber-400">{unverifiedUsers.length} utenti in attesa di verifica</p>
                </div>
              </div>
              <Button
                onClick={handleCleanupUnverified}
                disabled={cleaningUp}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
              >
                {cleaningUp ? (
                  <RefreshCw size={16} className="animate-spin mr-1" />
                ) : (
                  <Trash2 size={16} className="mr-1" />
                )}
                Pulisci Scaduti
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unverifiedUsers.map((u, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-white/5 rounded-xl">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-amber-600" />
                      <span className="text-sm font-medium text-[#1D1D1F] dark:text-white">{u.email}</span>
                      {u.expired && (
                        <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                          Scaduto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Registrato {u.hours_since_registration?.toFixed(1)}h fa • 
                      {u.days_until_deletion?.toFixed(1)} giorni prima dell'eliminazione auto
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteUser(u.user_id, u.email)}
                    className="p-2 text-[#86868B] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Elimina utente"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-4">
              Gli utenti hanno 1 ora per verificare la loro email. Dopo 5 giorni senza verifica, vengono eliminati automaticamente.
              "Pulisci Scaduti" elimina solo gli utenti con tempo scaduto (&gt;1 ora).
            </p>
          </div>
        )}

        {/* All Users Section */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-4 lg:p-6 border border-black/5 dark:border-white/10 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Users className="text-purple-500" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-[#1D1D1F] dark:text-white">Gestione Utenti</h3>
                <p className="text-xs text-[#86868B]">{allUsers.length} utenti totali</p>
              </div>
            </div>
            <Button
              onClick={() => setShowAllUsers(!showAllUsers)}
              variant="outline"
              size="sm"
              className="rounded-xl hidden lg:flex"
            >
              {showAllUsers ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
              {showAllUsers ? "Nascondi" : "Mostra tutti"}
            </Button>
          </div>
          
          {/* Search Bar - always visible */}
          <div className="relative mb-4">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
            <Input
              placeholder="Cerca utente..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!showAllUsers) setShowAllUsers(true);
              }}
              className="pl-10 rounded-xl bg-[#F5F5F7] dark:bg-white/5 border-0"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
              >
                <XCircle size={16} />
              </button>
            )}
          </div>
          
          {/* Mobile: Show first 5 users always, Desktop: controlled by showAllUsers */}
          <div className={`space-y-2 ${showAllUsers ? 'max-h-[500px]' : 'max-h-[300px] lg:max-h-0 lg:overflow-hidden'} overflow-y-auto transition-all`}>
            {(showAllUsers ? filteredUsers : filteredUsers.slice(0, 5)).map((u, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-[#F5F5F7] dark:bg-white/5 rounded-xl hover:bg-[#EBEBED] dark:hover:bg-white/10 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Mail size={14} className="text-[#86868B] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#1D1D1F] dark:text-white truncate">{u.email}</span>
                        {u.is_admin && (
                          <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full flex-shrink-0">
                            Admin
                          </span>
                        )}
                        {u.auth_provider === "google" && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full flex-shrink-0">
                            Google
                          </span>
                        )}
                        {u.email_verified ? (
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" title="Email verificata" />
                        ) : (
                          <XCircle size={14} className="text-red-500 flex-shrink-0" title="Email non verificata" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-[#86868B]">
                        <span className="truncate">{u.name || "Nessun nome"}</span>
                        <span className="flex items-center gap-1">
                          <Dog size={12} />
                          {u.pets_count || 0} animali
                        </span>
                        <span className="flex items-center gap-1">
                          <File size={12} />
                          {u.documents_count || 0} documenti
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatLastLogin(u.last_login)}
                        </span>
                      </div>
                    </div>
                    {!u.is_admin && u.email !== user?.email && (
                      <button
                        onClick={() => handleDeleteUser(u.user_id, u.email)}
                        className="p-2 text-[#86868B] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors ml-2 flex-shrink-0"
                        title="Elimina utente"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-[#86868B]">
                    <Search size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Nessun utente trovato</p>
                  </div>
                )}
              </div>
          
          {/* Show more button on mobile if not all shown */}
          {!showAllUsers && filteredUsers.length > 5 && (
            <button
              onClick={() => setShowAllUsers(true)}
              className="w-full mt-3 py-2 text-sm text-[#FF6B4A] font-medium hover:underline"
            >
              Mostra tutti gli utenti ({filteredUsers.length})
            </button>
          )}
          
          <p className="text-xs text-[#86868B] mt-4">
            Puoi eliminare qualsiasi utente (tranne admin e te stesso). L'eliminazione è permanente e rimuove tutti i dati associati.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10 mb-8">
          <h3 className="font-bold text-[#1D1D1F] dark:text-white mb-4">Azioni Rapide</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => navigate('/admin/professionals')}
              variant="outline"
              className="justify-start h-12 rounded-xl"
            >
              <Shield size={18} className="mr-3" />
              Gestisci Professionisti
              {stats?.pending_professionals > 0 && (
                <span className="ml-auto px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {stats.pending_professionals}
                </span>
              )}
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="justify-start h-12 rounded-xl"
            >
              <BarChart3 size={18} className="mr-3" />
              Torna alla Dashboard
            </Button>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Shield className="text-emerald-600" size={20} />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1">Privacy Utenti Protetta</h4>
              <p className="text-sm text-emerald-700 dark:text-emerald-400">
                Come admin puoi vedere solo le email degli utenti, mai i loro dati personali (indirizzo, CF, etc.) 
                né i dati dei loro animali. Questo per garantire la privacy secondo GDPR.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent className="max-w-[400px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="text-[#FF6B4A]" size={20} />
              Aggiungi Amministratore
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#1D1D1F] dark:text-white mb-2 block">
                Email dell'utente
              </label>
              <Input
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="email@esempio.com"
                className="h-12 rounded-xl"
                required
              />
              <p className="text-xs text-[#86868B] mt-2">
                L'utente deve essere già registrato su Zampa. Dopo essere stato aggiunto come admin, 
                dovrà usare il 2FA per accedere.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddAdmin(false)}
                className="flex-1 rounded-xl"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={addingAdmin}
                className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
              >
                {addingAdmin ? "Aggiunta..." : "Aggiungi Admin"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
